import { useEffect, useId, useMemo, useRef, useState, type ReactElement } from 'react';
import { trackFailed } from '../../../../../shared-web/track';
import type { EmberMedia } from '../api';
import './EmberMediaHeader.css';

/**
 * The Ember page's generated media: a fixed 16:9 header frame and a small
 * music on/off pill for the quiet music. The music starts as soon as it
 * exists; the pill is how the reader turns it off, or on when the browser
 * refused the start. While the image is still generating, the frame
 * holds a code-native pencil sketch; music has its own quiet composing mark.
 * Either finished asset can arrive independently. A music URL that will not load —
 * missing, blocked, undecodable — takes its pill down with it: a play button
 * over silence is a bug report, not a control.
 *
 * The <img> exists only once a resolved URL is in page state, so its ui.seen
 * (ember_media_image) records the arrival itself — the fact that separates
 * "the URL never reached the page" from "the image element failed", which
 * image_failed alone cannot.
 */
export function EmberMediaHeader({ media, alt = 'Ember illustration', draft = false, logEmberId, logConversationId }: {
  media?: EmberMedia;
  /** Short accessible name for the generated illustration. */
  alt?: string;
  draft?: boolean;
  /** The Ember the media belongs to — telemetry only. */
  logEmberId?: string;
  /** The telling the media belongs to (Result page) — telemetry only. */
  logConversationId?: string;
}): ReactElement | null {
  const imageUrl = media?.header_image_url || '';
  const imagePending = !imageUrl && media?.header_image_pending === true;
  const musicUrl = media?.music_url || '';
  const [imageReady, setImageReady] = useState(false);
  const [musicBroken, setMusicBroken] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);
  // A later poll can hand over a fresh URL; that one gets its own chance.
  useEffect(() => { setMusicBroken(false); }, [musicUrl]);
  // A memory-cached image can be complete before React's load listener is
  // live (a revisit of this page remounts the <img> onto a URL the browser
  // already holds), so onLoad alone would leave it at opacity 0 forever.
  useEffect(() => {
    const el = imgRef.current;
    setImageReady(Boolean(el && el.complete && el.naturalWidth > 0));
  }, [imageUrl]);
  // Stamped the render the URL arrives, on the same clock as the seen's t_ms.
  const imageMountT = useMemo(() => {
    if (!imageUrl) return 0;
    try { return Math.round(performance.now()); } catch { return 0; }
  }, [imageUrl]);

  const music = musicUrl && !musicBroken ? musicUrl : '';
  if (!imageUrl && !imagePending && !music) return null;
  const hasImageFrame = Boolean(imageUrl || imagePending);

  return (
    <div className={`ember-media-header${hasImageFrame ? '' : ' is-music-only'}${draft ? ' is-draft' : ''}`}>
      {hasImageFrame && (
        <div className="ember-media-frame">
          {(!imageUrl || !imageReady) && <CoverSketch />}
          {imageUrl && (
            <img
              ref={imgRef}
              className={`ember-media-image ember-media-generated${imageReady ? ' is-ready' : ''}`}
              src={imageUrl}
              alt={alt}
              loading="eager"
              decoding="async"
              onLoad={() => setImageReady(true)}
              onError={() => trackFailed('ember_media', 'image_failed', { ember_id: logEmberId })}
              data-log-seen="ember_media_image"
              data-log-state={draft ? 'draft' : 'final'}
              data-log-ember-id={logEmberId}
              data-log-conversation-id={logConversationId}
              data-log-mount-t-ms={imageMountT || undefined}
            />
          )}
        </div>
      )}
      {music && (
        <MusicToggle
          src={music}
          logEmberId={logEmberId}
          onBroken={() => {
            trackFailed('ember_media', 'music_broken', { ember_id: logEmberId });
            setMusicBroken(true);
          }}
        />
      )}
    </div>
  );
}

/* One continuous crayon line — house, door, window, chimney, smoke, ground,
 * grass, sun, rays, drawn in that order. The dash reveal and the pencil's
 * animateMotion both walk this exact path on the same 8s SMIL clock, so the
 * pencil tip stays on the line's end at any rendered size. */
const SKETCH_LINE =
  'M 251,431 C 249,393 250,352 251,315 L 227,321 C 268,286 310,252 353,221 C 394,252 436,287 477,320 L 455,315 C 456,353 455,393 454,431 ' +
  'M 331,431 C 330,410 330,388 331,368 C 337,352 365,352 371,368 C 372,388 372,410 371,431 ' +
  'M 277,340 L 309,338 L 311,370 L 278,372 L 277,341 ' +
  'M 421,272 L 420,231 L 444,230 L 445,291 ' +
  'M 434,222 C 424,204 446,196 440,178 C 434,160 454,152 448,134 ' +
  'M 150,437 C 260,430 380,440 500,434 C 560,431 610,435 648,433 ' +
  'M 180,434 L 186,414 L 191,433 L 197,412 L 202,433 ' +
  'M 588,435 L 594,416 L 599,434 L 605,414 L 610,434 ' +
  'M 748,84 C 772,84 792,102 790,126 C 788,152 770,168 746,168 C 722,168 706,150 706,126 C 706,102 726,85 750,88 ' +
  'M 748,62 L 748,40 M 795,80 L 810,64 M 812,128 L 834,128 M 793,174 L 807,189 M 701,80 L 686,64 M 684,128 L 662,128';

function SketchWashes(): ReactElement {
  return (
    <>
      <polygon points="352,232 246,314 462,314" fill="#cd7f57" fillOpacity="0.22" />
      <rect x="279" y="341" width="29" height="28" fill="#ecc878" fillOpacity="0.38" />
      <rect x="334" y="366" width="34" height="62" rx="14" fill="#b98a5a" fillOpacity="0.16" />
      <circle cx="748" cy="126" r="36" fill="#e9b45c" fillOpacity="0.32" />
    </>
  );
}

function SketchFilters({ crayonId, washId }: { crayonId: string; washId: string }): ReactElement {
  return (
    <defs>
      <filter id={crayonId} x="-8%" y="-8%" width="116%" height="116%">
        <feTurbulence type="fractalNoise" baseFrequency="0.022" numOctaves="3" seed="7" result="n" />
        <feDisplacementMap in="SourceGraphic" in2="n" scale="7" />
      </filter>
      <filter id={washId} x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur stdDeviation="7" />
      </filter>
    </defs>
  );
}

const SKETCH_STROKE = {
  fill: 'none',
  stroke: '#6f5b47',
  strokeOpacity: 0.62,
  strokeWidth: 5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

function CoverSketch(): ReactElement {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const lineId = `sketch-line-${uid}`;
  const crayonId = `sketch-crayon-${uid}`;
  const washId = `sketch-wash-${uid}`;
  return (
    <div className="ember-media-image ember-media-placeholder" role="img" aria-label="Cover image is being sketched">
      {/* An 8s loop: blank paper, the line drawn at constant speed, the pencil
        * lifts, soft crayon washes arrive, everything fades and starts over. */}
      <svg className="ember-media-sketch is-anim" viewBox="0 0 960 540" preserveAspectRatio="xMidYMid slice" aria-hidden>
        <SketchFilters crayonId={crayonId} washId={washId} />
        <g>
          <animate attributeName="opacity" values="0;1;1;0;0" keyTimes="0;0.03;0.92;0.975;1" dur="8s" repeatCount="indefinite" />
          <g filter={`url(#${washId})`} opacity="0">
            <animate attributeName="opacity" values="0;0;0.9;0.9" keyTimes="0;0.72;0.81;1" dur="8s" repeatCount="indefinite" />
            <SketchWashes />
          </g>
          <g filter={`url(#${crayonId})`}>
            <path id={lineId} pathLength={1} {...SKETCH_STROKE} strokeDasharray="1" strokeDashoffset="1" d={SKETCH_LINE}>
              <animate attributeName="stroke-dashoffset" values="1;1;0;0" keyTimes="0;0.04;0.72;1" calcMode="linear" dur="8s" repeatCount="indefinite" />
            </path>
          </g>
          <g>
            <animateMotion dur="8s" repeatCount="indefinite" calcMode="linear" keyPoints="0;0;1;1" keyTimes="0;0.04;0.72;1">
              <mpath href={`#${lineId}`} />
            </animateMotion>
            <animate attributeName="opacity" values="0;0;1;1;0;0" keyTimes="0;0.02;0.05;0.72;0.765;1" dur="8s" repeatCount="indefinite" />
            <animateTransform attributeName="transform" type="translate" additive="sum" values="0 0;0 0;-8 -18;-8 -18" keyTimes="0;0.72;0.765;1" dur="8s" repeatCount="indefinite" />
            <g transform="scale(1.5) translate(-6,-40)">
              <path d="M8 32 30 10l6 6-22 22-8 2z" fill="#9b5f35" />
              <path d="m6 40 2-8 6 6z" fill="#e1b76f" />
              <path d="m6 40 3-.8-2.2-2.2z" fill="#5f5147" />
            </g>
          </g>
        </g>
      </svg>
      {/* Reduced motion: the finished sketch, still, with its washes. */}
      <svg className="ember-media-sketch is-static" viewBox="0 0 960 540" preserveAspectRatio="xMidYMid slice" aria-hidden>
        <SketchFilters crayonId={`${crayonId}-s`} washId={`${washId}-s`} />
        <g filter={`url(#${washId}-s)`} opacity="0.9">
          <SketchWashes />
        </g>
        <g filter={`url(#${crayonId}-s)`}>
          <path {...SKETCH_STROKE} d={SKETCH_LINE} />
        </g>
      </svg>
      <span className="ember-media-placeholder-note">Sketching your cover</span>
    </div>
  );
}

// Ambience, not a track: the music sits under the reading voice, but present
// enough to be felt (0.1 → 0.2, 2026-09-12 拍板).
const MUSIC_VOLUME = 0.2;

function MusicToggle({ src, onBroken, logEmberId }: {
  src: string;
  onBroken: () => void;
  logEmberId?: string;
}): ReactElement {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const autoplayBlockedTracked = useRef(false);
  const [playing, setPlaying] = useState(false);

  // The music starts on its own. Browsers refuse audible autoplay on a cold
  // load — the tap that opened this link happened in the messaging app, not on
  // this document — and a refused start just leaves the pill in its off state
  // for the reader to tap. A pause() landing while this promise is pending
  // rejects it with AbortError, which is not the browser's refusal.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    audio.volume = MUSIC_VOLUME;
    audio.play().catch((error: unknown) => {
      if ((error as DOMException | null)?.name !== 'NotAllowedError') return;
      if (autoplayBlockedTracked.current) return;
      autoplayBlockedTracked.current = true;
      trackFailed('ember_media', 'autoplay_blocked', { ember_id: logEmberId });
    });
    return () => { audio.pause(); };
  }, [src]);

  const toggle = (): void => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.volume = MUSIC_VOLUME;
      audio.play().catch(() => { /* user can tap again */ });
    } else {
      audio.pause();
    }
  };

  return (
    <div className="ember-media-player is-arriving">
      <button
        type="button"
        className="ember-media-play"
        aria-label={playing ? 'Turn music off' : 'Turn music on'}
        aria-pressed={playing}
        data-log="ember_music_toggle"
        data-log-state={playing ? 'off' : 'on'}
        data-log-ember-id={logEmberId}
        onClick={toggle}
      >
        {/* Off is a plain note — there is music here, tap for it — and on is the
            music moving (2026-09-06 走查: a struck-through note as the resting
            state read as "muted / not allowed", when a refused autoplay is the
            common first state on a phone). */}
        {playing ? (
          <span className="ember-media-wave" aria-hidden><i /><i /><i /></span>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M9.5 17.3V6.4l9-1.9v10.9" />
            <circle cx="7" cy="17.3" r="2.5" fill="currentColor" stroke="none" />
            <circle cx="16" cy="15.4" r="2.5" fill="currentColor" stroke="none" />
          </svg>
        )}
      </button>
      <audio
        ref={audioRef}
        src={src}
        loop
        preload="auto"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onError={onBroken}
      />
    </div>
  );
}
